import type { parseFilterModel } from '@/lib/createAgGridDatasource';
import { buildDownloadOptions as buildDownloadOptionsShared, type SortColumnState } from '@/lib/downloadOptions';
import type { StudyProteoformsDownloadOptions } from '@/services/samples';
import type { ProteoformResultsTableConfig, SampleProteoformFilters } from '@/types';

import { tauEpitopeKeys, tauSamplesSortFields } from './tauSamplesColDefs';

// The fixed half of SampleProteoformFilters' keys. The epitope half is assay-defined and appended
// per request, the same way the column defs derive theirs — a static list would silently drop a
// filter on any epitope column the panel adds.
const FIXED_FILTER_FIELDS: readonly (keyof SampleProteoformFilters)[] = [
  'sample_name',
  'sample_group',
  'proteoform_name',
  'proteoform_abundance',
];

/** Both allow-lists and the threshold are panel-derived, not static: this grid's columns are the
 *  assay's epitopes plus four fixed ones, so hard-coding any of it would make a filtered download
 *  disagree with the grid it came from. Hence `tableConfig` is required, not optional. */
export function buildTauSamplesDownloadOptions(
  columnState: readonly SortColumnState[],
  filterModel: Parameters<typeof parseFilterModel>[0],
  tableConfig: ProteoformResultsTableConfig,
  assayId: string,
): StudyProteoformsDownloadOptions {
  const epitopeKeys = tauEpitopeKeys(tableConfig);

  const { sortBy, sortOrder, filters } = buildDownloadOptionsShared<string, SampleProteoformFilters>(
    columnState,
    filterModel,
    tauSamplesSortFields(tableConfig),
    [...FIXED_FILTER_FIELDS, ...epitopeKeys] as (keyof SampleProteoformFilters & string)[],
    // Every epitope column is a boolean set filter, and AG Grid serialises set values as strings —
    // without this the export sends "true"/"false" where the grid's own datasource sends booleans.
    new Set(epitopeKeys),
  );

  return { assayId, filters, sortBy, sortOrder, thresholdCpm: tableConfig.default_threshold_cpm };
}
